import { useState, useEffect } from 'react';
import { format, add, differenceInMinutes } from 'date-fns';

// Preset durations used by both components
export const PRESET_DURATIONS = [
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '1 hour', minutes: 60 },
];

// Day of week constants for formatting custom recurrence
export const DAYS_OF_WEEK = [
  { value: 'SU', label: 'S', fullName: 'Sunday' },
  { value: 'MO', label: 'M', fullName: 'Monday' },
  { value: 'TU', label: 'T', fullName: 'Tuesday' },
  { value: 'WE', label: 'W', fullName: 'Wednesday' },
  { value: 'TH', label: 'T', fullName: 'Thursday' },
  { value: 'FR', label: 'F', fullName: 'Friday' },
  { value: 'SA', label: 'S', fullName: 'Saturday' },
];

// Recurrence options shared across components
export const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom...' },
];

export interface UseDurationScheduleProps {
  initialDate?: Date;
  initialDuration?: number;
}

export function useDurationSchedule({
  initialDate,
  initialDuration,
}: UseDurationScheduleProps = {}) {
  const [date, setDate] = useState('');
  const [selectedDuration, setSelectedDuration] = useState<number>(30);
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // Initialize with provided values or defaults
  useEffect(() => {
    if (initialDate) {
      const dateStr = format(initialDate, 'yyyy-MM-dd');
      const timeStr = format(initialDate, 'HH:mm');
      const duration = initialDuration || 30;

      setDate(dateStr);
      setStartTime(timeStr);
      setSelectedDuration(duration);

      // Check if it's a preset duration
      const isPresetDuration = PRESET_DURATIONS.some(preset => preset.minutes === duration);
      setIsCustomDuration(!isPresetDuration);

      // Calculate end time based on duration
      const endDateTime = add(initialDate, { minutes: duration });
      const endTimeStr = format(endDateTime, 'HH:mm');

      setEndTime(endTimeStr);
    } else {
      // Initialize with current date/time
      const now = new Date();
      // Round to nearest 15 minute increment
      const minutes = now.getMinutes();
      const remainder = minutes % 15;
      if (remainder !== 0) {
        const roundTo = remainder < 8 ? -remainder : 15 - remainder;
        now.setMinutes(minutes + roundTo);
        now.setSeconds(0);
        now.setMilliseconds(0);
      }

      setDate(format(now, 'yyyy-MM-dd'));
      setStartTime(format(now, 'HH:mm'));

      const duration = initialDuration || 30;
      setSelectedDuration(duration);

      const endDateTime = add(now, { minutes: duration });
      setEndTime(format(endDateTime, 'HH:mm'));
    }
  }, [initialDate, initialDuration]);

  // Ensure endTime is always calculated when date/time/duration changes
  useEffect(() => {
    // Skip only if we're missing required date/time values
    if (!date || !startTime) {
      return;
    }

    // Calculate endTime based on current values
    const startDateTime = new Date(`${date}T${startTime}`);
    const endDateTime = add(startDateTime, { minutes: selectedDuration });
    const newEndTime = format(endDateTime, 'HH:mm');

    setEndTime(newEndTime);
  }, [date, startTime, selectedDuration]);

  // Handle duration chip selection
  const handleDurationSelect = (minutes: number) => {
    setSelectedDuration(minutes);

    // Check if this is a preset duration
    const isPreset = PRESET_DURATIONS.some(preset => preset.minutes === minutes);
    setIsCustomDuration(!isPreset);

    // Auto-calculate end time based on selected duration
    if (date && startTime) {
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = add(startDateTime, { minutes });
      const newEndTime = format(endDateTime, 'HH:mm');
      setEndTime(newEndTime);
    }
  };

  // Enable custom duration mode
  const handleCustomDuration = () => {
    setIsCustomDuration(true);

    // Initialize custom duration fields with current values
    if (date && startTime) {
      const endDateTime = add(new Date(`${date}T${startTime}`), {
        minutes: selectedDuration || 30,
      });
      setEndTime(format(endDateTime, 'HH:mm'));
    }
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);

    // When in custom duration mode, recalculate the duration based on fixed end time
    if (isCustomDuration && date && endTime) {
      const startDateTime = new Date(`${date}T${newStartTime}`);
      const endDateTime = new Date(`${date}T${endTime}`);

      // Only update if end time is after start time
      if (endDateTime > startDateTime) {
        const calculatedDuration = differenceInMinutes(endDateTime, startDateTime);
        setSelectedDuration(calculatedDuration);
      }
    }
    // When NOT in custom mode, the useEffect will handle updating endTime
  };

  // Handle end time change
  const handleEndTimeChange = (newEndTime: string) => {
    setEndTime(newEndTime);

    // Recalculate duration when end time changes in custom mode
    if (isCustomDuration && date && startTime) {
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = new Date(`${date}T${newEndTime}`);

      // Only update if end time is after start time
      if (endDateTime > startDateTime) {
        const calculatedDuration = differenceInMinutes(endDateTime, startDateTime);
        setSelectedDuration(calculatedDuration);
      }
    }
  };

  // Handle date change
  const handleDateChange = (newDate: string) => {
    setDate(newDate);
  };

  // Get the current appointment date and duration
  const getAppointmentDateTime = () => {
    if (!date || !startTime) return { startDateTime: undefined, duration: undefined };

    const startDateTime = new Date(`${date}T${startTime}`);
    return {
      startDateTime,
      duration: selectedDuration,
    };
  };

  return {
    date,
    startTime,
    endTime,
    selectedDuration,
    isCustomDuration,
    setDate,
    setStartTime,
    setEndTime,
    setSelectedDuration,
    setIsCustomDuration,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleDurationSelect,
    handleCustomDuration,
    getAppointmentDateTime,
  };
}

export type UseDurationScheduleReturn = ReturnType<typeof useDurationSchedule>;
